import { useEffect, useState } from "react";
import {
  Box,
  Heading,
  Avatar,
  Stack,
  Text,
  FormControl,
  FormLabel,
  Input,
  Select,
  Button,
  useToast,
  Card,
  CardBody,
  Divider,
} from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import { getEmployeeData, getEmployeeDataMe, updateEmployee } from "../../hooks/employees";

interface Employee {
  id: string;
  user: {
    first_name: string;
    last_name: string;
    email: string;
  };
  role: string;
  organization: {
    name: string;
  };
}

export default function EditTeamMemberProfile() {
  const { orgId, teamMemberId } = useParams();
  const [employee, setEmployee] = useState<Employee | null>(null);
  const [meEmployee, setMeEmployee] = useState<Employee>();
  const [role, setRole] = useState("");
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      const response = await getEmployeeData(orgId, teamMemberId);
      if (response?.success) {
        setEmployee(response.data);
        setRole(response.data.role);
      }
    };
    fetchData();
    getMyEmployeeMeData(orgId);
  }, [orgId, teamMemberId]);

  const getMyEmployeeMeData = async (id: any) => {
    setLoading(true);
    let response = await getEmployeeDataMe(id);
    if(response.success === true && response.data){
        setMeEmployee(response.data);
        console.log('me data', response.data);
        setLoading(false);
    }else{
        toast({ title: "Failed to load your employee data", status: "error" });
        setLoading(false);
    }
    return;
};

  const handleSubmit = async () => {
    if (!employee) return;
    if (!orgId){
        toast({ title: "Organization ID is missing", status: "error" });
        return;
    }
    setLoading(true);

    // 2️⃣ Check access control
    if (meEmployee?.role !== "admin" && meEmployee?.role !== "manager") {
      toast({
        title: "Access Denied",
        description: "Only Admins or Managers can update roles.",
        status: "error",
        duration: 3500,
        isClosable: true,
      });
      setLoading(false);
      return;
    }

    const response = await updateEmployee(orgId, employee.id, { role });
    setLoading(false);

    if (response.success) {
      toast({
        title: "Access Level Updated",
        description: "Employee role updated successfully.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      navigate(`/organization/${orgId}/team/${employee.id}`);
    } else {
      toast({
        title: "Update Failed",
        description: "Something went wrong, please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  if (!employee) return <Text textAlign="center">Loading...</Text>;

  return (
    <Box maxW="lg" mx="auto" mt={10}>
      <Card boxShadow="xl" borderRadius="2xl">
        <CardBody>
          <Stack align="center" spacing={4}>
            <Avatar bg={"#006400"} size="xl" name={`${employee.user.first_name} ${employee.user.last_name}`} />
            <Heading size="md">
              {employee.user.first_name} {employee.user.last_name}
            </Heading>
            <Text color="gray.600">{employee.user.email}</Text>
            <Text fontSize="sm" color="blue.500" fontWeight="semibold">
              {employee.organization?.name}
            </Text>
          </Stack>

          <Divider my={6} />

          <Stack spacing={5}>
            <FormControl>
              <FormLabel>First Name</FormLabel>
              <Input value={employee.user.first_name} isDisabled />
            </FormControl>

            <FormControl>
              <FormLabel>Last Name</FormLabel>
              <Input value={employee.user.last_name} isDisabled />
            </FormControl>

            <FormControl>
              <FormLabel>Email</FormLabel>
              <Input value={employee.user.email} isDisabled />
            </FormControl>

            <FormControl>
              <FormLabel>Access Level</FormLabel>
              <Select value={role} onChange={(e) => setRole(e.target.value)}>
                <option value="manager">Manager</option>
                <option value="sales">Sales Representative</option>
                <option value="marketing">Marketing</option>
                <option value="finance">Finance</option>
                <option value="support">Support Staff</option>
                <option value="other">Other</option>
              </Select>
            </FormControl>

            <Button
              mt={4}
              colorScheme="green"
              bg={"#006400"}
              onClick={handleSubmit}
              isLoading={loading}
              loadingText="Updating..."
            >
              Save Changes
            </Button>
          </Stack>
        </CardBody>
      </Card>
    </Box>
  );
}
